import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  Paper,
  Grid,
  Typography,
  Divider,
} from "@mui/material";
import {
  Person,
  Email,
  LocationOn,
  Phone,
  AssignmentInd,
  Visibility,
  VisibilityOff,
  DirectionsCar,
} from "@mui/icons-material";
import { Users, Vehicle } from "./userinterface";

interface EditUserDialogProps {
  open: boolean;
  onClose: () => void;
  user: Users | null;
  onUserUpdated: () => void;
  vehicles: Vehicle[];
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  onClose,
  user,
  onUserUpdated,
  vehicles,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    phone_number: "",
    fullName: "",
    is_under: 4,
    password: "",
  });
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<number[]>([]);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dialogContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        address: user.address || "",
        email: user.email || "",
        phone_number: user.phone_number || "",
        fullName: user.fullName || "",
        is_under: 4,
        password: "",
      });

      // Explicitly type the vehicles array
      const userVehicles: Vehicle[] = user.vehicles || [];
      setSelectedVehicleIds(userVehicles.map((v: Vehicle) => v.id));
    }
  }, [user]);

  const filteredVehicles = vehicles.filter(
    (vehicle) =>
      (vehicle.plateNo?.toLowerCase().includes(vehicleSearch.toLowerCase()) ||
        vehicle.vin?.toLowerCase().includes(vehicleSearch.toLowerCase())) &&
      !selectedVehicleIds.includes(vehicle.id)
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!user) return;

    const updatedUser = {
      ...formData,
      vehicle_ids: selectedVehicleIds,
    };

    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${process.env.REACT_APP_API_BASE_URL}/user/${user.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": token,
      },
      body: JSON.stringify(updatedUser),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.message) {
          console.error("Error updating user:", data.message);
        } else {
          onUserUpdated();
          onClose();
        }
      })
      .catch((error) => console.error("Error updating user:", error));
  };

  const styles = {
    dialogPaper: {
      backgroundColor: "#1e1e1e",
      border: "2px solid #4caf50",
      borderRadius: "16px",
      width: "600px",
      maxWidth: "calc(100% - 40px)",
      maxHeight: "90vh",
    },
    dialogTitle: {
      color: "#4caf50",
      textAlign: "center",
      padding: "24px",
      borderBottom: "1px solid rgba(76, 175, 80, 0.3)",
    },
    dialogContent: {
      padding: "24px",
      backgroundColor: "#1e1e1e",
      overflowY: "auto",
      "&::-webkit-scrollbar": { width: "8px" },
      "&::-webkit-scrollbar-thumb": {
        background: "#4caf50",
        borderRadius: "4px",
      },
    },
    textField: {
      "& .MuiInputBase-input": { color: "#ffffff" },
      "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
      "& .MuiOutlinedInput-root": {
        "& fieldset": { borderColor: "rgba(255, 255, 255, 0.23)" },
        "&:hover fieldset": { borderColor: "#4caf50" },
        "&.Mui-focused fieldset": { borderColor: "#4caf50" },
      },
    },
    chip: {
      backgroundColor: "#2e7d32",
      color: "white",
      "& .MuiChip-deleteIcon": {
        color: "rgba(255, 255, 255, 0.7)",
        "&:hover": { color: "white" },
      },
    },
    dropdown: {
      position: "absolute",
      width: "100%",
      zIndex: 1,
      mt: 1,
      backgroundColor: "#2d2d2d",
      maxHeight: "200px",
      overflowY: "auto",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
      borderRadius: "8px",
      "&::-webkit-scrollbar": { width: "6px" },
      "&::-webkit-scrollbar-thumb": {
        background: "#4caf50",
        borderRadius: "3px",
      },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: styles.dialogPaper }}
      maxWidth="md"
    >
      <DialogTitle sx={styles.dialogTitle}>Edit User</DialogTitle>
      <DialogContent sx={styles.dialogContent} ref={dialogContentRef}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="fullName"
              label="Full Name"
              value={formData.fullName}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                  </InputAdornment>
                ),
              }}
              sx={styles.textField}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="name"
              label="Username"
              value={formData.name}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AssignmentInd sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                  </InputAdornment>
                ),
              }}
              sx={styles.textField}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="email"
              label="Email"
              value={formData.email}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                  </InputAdornment>
                ),
              }}
              sx={styles.textField}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="phone_number"
              label="Phone Number"
              value={formData.phone_number}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                  </InputAdornment>
                ),
              }}
              sx={styles.textField}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="address"
              label="Address"
              value={formData.address}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                  </InputAdornment>
                ),
              }}
              sx={styles.textField}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="password"
              label="New Password (leave blank to keep current)"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="start"
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={styles.textField}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: "rgba(255, 255, 255, 0.12)" }} />

        <Box sx={{ position: "relative" }}>
          <Typography variant="h6" sx={{ color: "#4caf50", mb: 2 }}>
            Vehicle Assignment
          </Typography>

          {selectedVehicleIds.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {vehicles
                .filter((v) => selectedVehicleIds.includes(v.id))
                .map((vehicle) => (
                  <Chip
                    key={vehicle.id}
                    label={`${vehicle.plateNo || "No plate"} `}
                    onDelete={() =>
                      setSelectedVehicleIds((prev) =>
                        prev.filter((id) => id !== vehicle.id)
                      )
                    }
                    sx={styles.chip}
                  />
                ))}
            </Box>
          )}

          <TextField
            fullWidth
            label="Search vehicles by plate or VIN"
            value={vehicleSearch}
            onChange={(e) => {
              setVehicleSearch(e.target.value);
              setShowVehicleDropdown(true);
            }}
            onFocus={() => setShowVehicleDropdown(true)}
            onBlur={() => setTimeout(() => setShowVehicleDropdown(false), 200)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DirectionsCar sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                </InputAdornment>
              ),
            }}
            sx={styles.textField}
          />

          {showVehicleDropdown && filteredVehicles.length > 0 && (
            <Paper sx={styles.dropdown}>
              {filteredVehicles.map((vehicle) => (
                <MenuItem
                  key={vehicle.id}
                  onClick={() => {
                    setSelectedVehicleIds((prev) => [...prev, vehicle.id]);
                    setVehicleSearch("");
                  }}
                  sx={{ color: "white" }}
                >
                  {vehicle.plateNo || "No plate"} - {vehicle.vin || "No VIN"}
                  <Typography
                    variant="caption"
                    sx={{ ml: 1, color: "#aaa" }}
                  ></Typography>
                </MenuItem>
              ))}
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 3, borderTop: "1px solid rgba(76, 175, 80, 0.3)" }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: "#f44336",
            border: "1px solid #f44336",
            "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.1)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          sx={{
            backgroundColor: "#4caf50",
            color: "#000",
            "&:hover": { backgroundColor: "#3d8b40" },
          }}
        >
          Update User
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
